import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import { ActionMenuItem, Button, Icon, Input, Modal, Select, useToast } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { errorMessage } from '@/hooks/useApiError';

/**
 * The two decisions an admin can make about an account, wherever it appears.
 *
 * They are deliberately not one "change status" dropdown of enum names. The
 * platform allows four states, but an admin is only ever making one of two
 * decisions, and they are not the same kind of decision:
 *
 *   1. Activate / deactivate — housekeeping. Reversible, no accusation in it,
 *      no reason needed.
 *   2. Suspend / ban — a sanction. Ends every session on the spot, demands a
 *      reason, and goes into the audit log against the admin who did it.
 *
 * A single list mixing the four made "deactivate" and "ban" one click apart
 * and equally weighted, which is how the wrong one gets picked.
 *
 * `account` is the *account*: `{ id, full_name, status, role }` — a user id,
 * never a company member or student profile id.
 */

/** Where the reversible button goes from here, and what it should be called. */
const TOGGLE = {
  // Deactivating is switched off for now; restore this line to bring the
  // "Deactivate account" button back. Activate and Reinstate stay, so an
  // account that is already inactive, suspended or banned can still be undone.
  // ACTIVE: { status: 'INACTIVE', label: 'Deactivate' },
  INACTIVE: { status: 'ACTIVE', label: 'Activate' },
  PENDING_VERIFICATION: { status: 'ACTIVE', label: 'Activate' },
  SUSPENDED: { status: 'ACTIVE', label: 'Reinstate' },
  BANNED: { status: 'ACTIVE', label: 'Reinstate' },
};

/**
 * How the reversible button looks on a record header. Taking access away is
 * housekeeping, so it stays neutral; giving it back is the good news.
 */
const TOGGLE_LOOK = {
  INACTIVE: { variant: 'secondary', icon: 'power' },
  ACTIVE: { variant: 'tintPositive', icon: 'restore' },
};

const TOGGLE_COPY = {
  INACTIVE:
    'Signs them out and blocks new sign-ins. Nothing is deleted and it can be undone at any time.',
  ACTIVE: 'Restores access. They can sign in again immediately.',
};

const RESTRICT_COPY = {
  SUSPENDED:
    'Ends their sessions immediately and blocks sign-in. Meant to be temporary — reinstating is one click.',
  BANNED:
    'Ends their sessions immediately and blocks sign-in. Meant to be permanent. Their listings, applications and messages stay where they are.',
};

/**
 * `compact`: short labels ("Activate", "Suspend or ban") for a card footer.
 * `menu`: rendered as items of the surrounding `ActionMenu` ("More").
 */
export function AccountActions({ account, size = 'sm', compact = false, menu = false, className }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState(null);
  const [restrictTo, setRestrictTo] = useState('SUSPENDED');
  const [reason, setReason] = useState('');

  const change = useMutation({
    mutationFn: ({ status, reason: text }) =>
      adminApi.setUserStatus(account.id, status, text.trim() || undefined),
    onSuccess: (updated) => {
      toast.success(`${updated.full_name} is now ${updated.status.toLowerCase()}`);
      // Every admin screen shows an account state somewhere -- the directories,
      // the records, the overview counters -- so refresh the console rather
      // than guessing which caller mounted this.
      void queryClient.invalidateQueries({
        predicate: (query) => String(query.queryKey[0]).startsWith('admin-'),
      });
      close();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not change the account status')),
  });

  function close() {
    setDialog(null);
    setReason('');
  }

  // The backend refuses all three of these, and a button that only ever
  // returns an error is worse than no button.
  const moderatable =
    account?.id &&
    account.role !== 'SUPER_ADMIN' &&
    account.id !== user?.id &&
    account.status !== 'DELETED';
  if (!moderatable) return null;

  const toggle = TOGGLE[account.status];
  const restrictable = account.status !== 'BANNED';
  const restrictOptions = account.status === 'SUSPENDED' ? ['BANNED'] : ['SUSPENDED', 'BANNED'];

  function openRestrict() {
    setRestrictTo(restrictOptions[0]);
    setReason('');
    setDialog('restrict');
  }

  const restrictLabel = account.status === 'SUSPENDED' ? 'Ban' : 'Suspend or ban';

  return (
    <div className={menu ? 'contents' : (className ?? 'flex flex-wrap items-center gap-2')}>
      {toggle &&
        (menu ? (
          <ActionMenuItem
            icon={TOGGLE_LOOK[toggle.status].icon}
            title={TOGGLE_COPY[toggle.status]}
            onSelect={() => setDialog('toggle')}
          >
            {toggle.label} account
          </ActionMenuItem>
        ) : (
          <Button
            variant={TOGGLE_LOOK[toggle.status].variant}
            size={size}
            onClick={() => setDialog('toggle')}
            title={TOGGLE_COPY[toggle.status]}
          >
            <Icon name={TOGGLE_LOOK[toggle.status].icon} size={compact ? 14 : 15} />
            {compact ? toggle.label : `${toggle.label} account`}
          </Button>
        ))}
      {restrictable &&
        (menu ? (
          <ActionMenuItem icon="suspended" tone="danger" onSelect={openRestrict}>
            {restrictLabel}
          </ActionMenuItem>
        ) : (
          // A sanction, so it carries the warning colour -- but not the filled
          // red of the confirm button inside, which is the point of no return.
          <Button
            variant="tintWarning"
            size={size}
            onClick={openRestrict}
            title="Block sign-in temporarily or permanently, with a reason"
          >
            <Icon name="suspended" size={compact ? 14 : 15} />
            {restrictLabel}
          </Button>
        ))}

      <Modal
        open={dialog === 'toggle'}
        title={`${toggle?.label} ${account.full_name}`}
        description={TOGGLE_COPY[toggle?.status]}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={change.isPending}>
              Cancel
            </Button>
            <Button
              isLoading={change.isPending}
              onClick={() => change.mutate({ status: toggle.status, reason })}
            >
              {toggle?.label}
            </Button>
          </>
        }
      >
        <Input
          label="Reason"
          hint="Optional. Recorded in the audit log against your account."
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>

      <Modal
        open={dialog === 'restrict'}
        title={`Restrict ${account.full_name}`}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={change.isPending}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={!reason.trim()}
              isLoading={change.isPending}
              onClick={() => change.mutate({ status: restrictTo, reason })}
            >
              {restrictTo === 'BANNED' ? 'Ban account' : 'Suspend account'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {restrictOptions.length > 1 ? (
            <Select
              label="Sanction"
              value={restrictTo}
              onChange={(event) => setRestrictTo(event.target.value)}
            >
              <option value="SUSPENDED">Suspend — temporary</option>
              <option value="BANNED">Ban — permanent</option>
            </Select>
          ) : (
            <p className="text-ink-muted text-sm">This account is already suspended.</p>
          )}

          <p className="text-ink-muted text-sm">{RESTRICT_COPY[restrictTo]}</p>

          <Input
            label="Reason"
            required
            hint="Required, and recorded in the audit log against your account."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}
